import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { Star, AlertCircle, CheckCircle, Clock, MessageSquare, Send, X, Eye, ThumbsUp, ThumbsDown } from 'lucide-react';

interface Review {
  id: string;
  platform: string;
  brand: string;
  location_id: string;
  review_id: string;
  rating: number | null;
  author: string | null;
  text: string | null;
  created_at: string;
  responded_at: string | null;
  response_text: string | null;
  response_status: string;
}

interface ReviewAlert {
  id: string;
  review_id: string;
  alert_type: string;
  severity: string;
  message: string;
  is_read: boolean;
  created_at: string;
  review: Review;
}

interface ResponseQueueItem {
  id: string;
  review_id: string;
  draft_response: string;
  confidence_score: number | null;
  requires_approval: boolean;
  status: string;
  created_at: string;
  review: Review;
}

interface Stats {
  totalReviews: number;
  avgRating: string;
  ratingDistribution: { [key: number]: number };
  responseRate: string;
  avgResponseTimeHours: string;
  unreadAlerts: number;
}

const ReviewsMonitorPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'reviews' | 'alerts' | 'responses'>('alerts');
  const [reviews, setReviews] = useState<Review[]>([]);
  const [alerts, setAlerts] = useState<ReviewAlert[]>([]);
  const [responseQueue, setResponseQueue] = useState<ResponseQueueItem[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [selectedResponse, setSelectedResponse] = useState<ResponseQueueItem | null>(null);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);

  const API_URL = import.meta.env.VITE_MARKETING_API_URL || 'http://localhost:3004';

  useEffect(() => {
    loadData();
    // Set up real-time subscription for new reviews
    const subscription = supabase
      .channel('reviews_monitor')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'marketing_reviews'
        },
        () => {
          loadData();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [selectedBrand]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadAlerts(),
        loadResponseQueue(),
        loadReviews(),
        loadStats()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadAlerts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reviews/alerts?is_read=false`);
      const data = await response.json();
      setAlerts(data.alerts || []);
    } catch (err) {
      console.error('Error loading alerts:', err);
    }
  };

  const loadResponseQueue = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reviews/response-queue?status=pending_approval`);
      const data = await response.json();
      setResponseQueue(data.responses || []);
    } catch (err) {
      console.error('Error loading response queue:', err);
    }
  };

  const loadReviews = async () => {
    try {
      const brandParam = selectedBrand !== 'all' ? `&brand=${selectedBrand}` : '';
      const response = await fetch(`${API_URL}/api/reviews?limit=50${brandParam}`);
      const data = await response.json();
      setReviews(data.reviews || []);
    } catch (err) {
      console.error('Error loading reviews:', err);
    }
  };

  const loadStats = async () => {
    try {
      const brandParam = selectedBrand !== 'all' ? `?brand=${selectedBrand}` : '';
      const response = await fetch(`${API_URL}/api/reviews/stats${brandParam}`);
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const markAlertRead = async (alertId: string) => {
    try {
      await fetch(`${API_URL}/api/reviews/alerts/${alertId}/read`, {
        method: 'PUT'
      });
      loadAlerts();
      loadStats();
    } catch (err) {
      console.error('Error marking alert as read:', err);
    }
  };

  const dismissAlert = async (alertId: string) => {
    try {
      await fetch(`${API_URL}/api/reviews/alerts/${alertId}/dismiss`, {
        method: 'PUT'
      });
      loadAlerts();
      loadStats();
    } catch (err) {
      console.error('Error dismissing alert:', err);
    }
  };

  const approveResponse = async (responseId: string) => {
    try {
      await fetch(`${API_URL}/api/reviews/response-queue/${responseId}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: null })
      });
      loadResponseQueue();
      setSelectedResponse(null);
    } catch (err) {
      console.error('Error approving response:', err);
      alert('Failed to approve response');
    }
  };

  const rejectResponse = async (responseId: string, reason: string) => {
    try {
      await fetch(`${API_URL}/api/reviews/response-queue/${responseId}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: null, reason })
      });
      loadResponseQueue();
      setSelectedResponse(null);
    } catch (err) {
      console.error('Error rejecting response:', err);
      alert('Failed to reject response');
    }
  };

  const postResponse = async (responseId: string) => {
    if (!confirm('Post this response to Google My Business?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/reviews/response-queue/${responseId}/post`, {
        method: 'POST'
      });
      const data = await response.json();
      
      if (data.success) {
        alert('Response posted successfully!');
        loadResponseQueue();
        loadReviews();
      } else {
        alert('Failed to post response: ' + data.error);
      }
    } catch (err) {
      console.error('Error posting response:', err);
      alert('Failed to post response');
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'text-red-600 bg-red-50 border-red-200';
      case 'warning': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      default: return 'text-blue-600 bg-blue-50 border-blue-200';
    }
  };

  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'critical': return <AlertCircle className="w-5 h-5" />;
      case 'warning': return <Clock className="w-5 h-5" />;
      default: return <CheckCircle className="w-5 h-5" />;
    }
  };

  const renderStars = (rating: number | null) => {
    if (!rating) return <span className="text-gray-400">No rating</span>;
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map(star => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
          />
        ))}
      </div>
    );
  };

  const brandColors = {
    halesac: 'bg-blue-100 text-blue-800',
    myccs: 'bg-green-100 text-green-800',
    abrahamac: 'bg-purple-100 text-purple-800'
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Reviews Monitor</h1>
          <p className="text-gray-600 mt-1">Real-time Google My Business reviews monitoring and response system</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedBrand}
            onChange={(e) => setSelectedBrand(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Brands</option>
            <option value="halesac">Hales AC</option>
            <option value="myccs">Climate Control</option>
            <option value="abrahamac">Abraham AC</option>
          </select>
        </div>
      </div>

      {/* Stats Dashboard */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Total Reviews (30d)</div>
            <div className="text-3xl font-bold mt-1">{stats.totalReviews}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Average Rating</div>
            <div className="text-3xl font-bold mt-1 text-yellow-600">{stats.avgRating}</div>
            <div className="flex mt-2">
              {[1, 2, 3, 4, 5].map(star => (
                <Star key={star} className={`w-4 h-4 ${star <= parseFloat(stats.avgRating) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
              ))}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Response Rate</div>
            <div className="text-3xl font-bold mt-1 text-green-600">{stats.responseRate}%</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Avg Response Time</div>
            <div className="text-3xl font-bold mt-1">{stats.avgResponseTimeHours}h</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600">Unread Alerts</div>
            <div className="text-3xl font-bold mt-1 text-red-600">{stats.unreadAlerts}</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'alerts', label: 'Alerts', count: alerts.length },
            { id: 'responses', label: 'Response Queue', count: responseQueue.length },
            { id: 'reviews', label: 'All Reviews', count: reviews.length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
              {tab.count > 0 && (
                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                  activeTab === tab.id ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">Loading...</div>
        </div>
      ) : (
        <>
          {/* Alerts Tab */}
          {activeTab === 'alerts' && (
            <div className="space-y-4">
              {alerts.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                  No unread alerts
                </div>
              ) : (
                alerts.map(alert => (
                  <div key={alert.id} className={`bg-white rounded-lg shadow border-l-4 p-4 ${getSeverityColor(alert.severity)}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3 flex-1">
                        {getSeverityIcon(alert.severity)}
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`text-xs px-2 py-1 rounded ${brandColors[alert.review.brand as keyof typeof brandColors] || 'bg-gray-100 text-gray-800'}`}>
                              {alert.review.brand}
                            </span>
                            <span className="text-xs text-gray-500">{new Date(alert.created_at).toLocaleString()}</span>
                          </div>
                          <p className="font-medium mb-2">{alert.message}</p>
                          <div className="bg-gray-50 rounded p-3 mb-3">
                            <div className="flex items-center gap-2 mb-2">
                              {renderStars(alert.review.rating)}
                              <span className="text-sm font-medium">{alert.review.author}</span>
                            </div>
                            <p className="text-sm text-gray-700">{alert.review.text || 'No review text'}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          onClick={() => markAlertRead(alert.id)}
                          variant="secondary"
                          size="sm"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => dismissAlert(alert.id)}
                          variant="secondary"
                          size="sm"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                        <Button
                          onClick={() => setSelectedReview(alert.review)}
                          size="sm"
                        >
                          View Details
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Response Queue Tab */}
          {activeTab === 'responses' && (
            <div className="space-y-4">
              {responseQueue.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                  No responses pending approval
                </div>
              ) : (
                responseQueue.map(response => (
                  <div key={response.id} className="bg-white rounded-lg shadow p-4">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs px-2 py-1 rounded ${brandColors[response.review.brand as keyof typeof brandColors] || 'bg-gray-100 text-gray-800'}`}>
                          {response.review.brand}
                        </span>
                        {response.confidence_score && (
                          <span className="text-xs text-gray-500">
                            Confidence: {(response.confidence_score * 100).toFixed(0)}%
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {new Date(response.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <div className="bg-gray-50 rounded p-3 mb-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-gray-600">Original Review:</span>
                        {renderStars(response.review.rating)}
                        <span className="text-sm font-medium">{response.review.author}</span>
                      </div>
                      <p className="text-sm text-gray-700">{response.review.text || 'No review text'}</p>
                    </div>

                    <div className="bg-blue-50 rounded p-3 mb-3">
                      <div className="text-xs font-medium text-gray-600 mb-2">Proposed Response:</div>
                      <p className="text-sm text-gray-800">{response.draft_response}</p>
                    </div>

                    <div className="flex items-center gap-2 justify-end">
                      <Button
                        onClick={() => rejectResponse(response.id, 'Rejected by user')}
                        variant="secondary"
                      >
                        <ThumbsDown className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                      <Button
                        onClick={() => approveResponse(response.id)}
                      >
                        <ThumbsUp className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => setSelectedResponse(response)}
                        variant="secondary"
                      >
                        Edit & Post
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* All Reviews Tab */}
          {activeTab === 'reviews' && (
            <div className="space-y-4">
              {reviews.map(review => (
                <div key={review.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs px-2 py-1 rounded ${brandColors[review.brand as keyof typeof brandColors] || 'bg-gray-100 text-gray-800'}`}>
                        {review.brand}
                      </span>
                      {renderStars(review.rating)}
                      <span className="text-sm font-medium">{review.author}</span>
                      <span className="text-xs text-gray-500">{new Date(review.created_at).toLocaleString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {review.responded_at && (
                        <span className="text-xs px-2 py-1 rounded bg-green-100 text-green-800">
                          Responded
                        </span>
                      )}
                      <span className={`text-xs px-2 py-1 rounded ${
                        review.platform === 'gbp' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {review.platform.toUpperCase()}
                      </span>
                    </div>
                  </div>
                  <p className="text-gray-700 mb-3">{review.text || 'No review text'}</p>
                  {review.response_text && (
                    <div className="bg-gray-50 rounded p-3 border-l-4 border-green-500">
                      <div className="text-xs font-medium text-gray-600 mb-1">Your Response:</div>
                      <p className="text-sm text-gray-700">{review.response_text}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Response Edit Modal */}
      {selectedResponse && (
        <Modal
          isOpen={true}
          onClose={() => setSelectedResponse(null)}
          title="Review and Post Response"
        >
          <div className="space-y-4">
            <div className="bg-gray-50 rounded p-3">
              <div className="text-xs font-medium text-gray-600 mb-2">Original Review:</div>
              <div className="flex items-center gap-2 mb-2">
                {renderStars(selectedResponse.review.rating)}
                <span className="text-sm font-medium">{selectedResponse.review.author}</span>
              </div>
              <p className="text-sm text-gray-700">{selectedResponse.review.text || 'No review text'}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Response Text:</label>
              <textarea
                value={selectedResponse.draft_response}
                readOnly
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={6}
              />
            </div>

            <div className="flex items-center gap-3 justify-end">
              <Button onClick={() => setSelectedResponse(null)} variant="secondary">
                Cancel
              </Button>
              <Button onClick={() => postResponse(selectedResponse.id)}>
                <Send className="w-4 h-4 mr-2" />
                Post to Google
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default ReviewsMonitorPage;
